import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import type { StayAiIntervalUnit } from "@/lib/api";

const ALLOWED_UNITS: readonly StayAiIntervalUnit[] = [
  "day",
  "week",
  "month",
  "year",
];

function normalizeIntervalUnit(
  raw: string | null | undefined,
): StayAiIntervalUnit | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return ALLOWED_UNITS.includes(lower as StayAiIntervalUnit)
    ? (lower as StayAiIntervalUnit)
    : null;
}

function unitLabel(unit: StayAiIntervalUnit, count: number): string {
  return count === 1 ? unit : `${unit}s`;
}

export function ChangeFrequencyDialog({
  open,
  onOpenChange,
  currentInterval,
  currentIntervalUnit,
  onConfirm,
  confirmPending,
  actionIsPrimary = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentInterval: number | null;
  currentIntervalUnit: string | null;
  onConfirm: (interval: number, intervalUnit: StayAiIntervalUnit) => void;
  confirmPending: boolean;
  /**
   * When true (e.g. opened from the cancel/retention flow), the action is the
   * filled primary button and "Never mind" is the outline secondary. Default
   * keeps "Never mind" primary (proactive use from the manage sheet).
   */
  actionIsPrimary?: boolean;
}) {
  const actionVariant = actionIsPrimary ? "default" : "outline";
  const actionPillClass = actionIsPrimary ? PILL_FILLED_CLASS : PILL_OUTLINE_CLASS;
  const dismissVariant = actionIsPrimary ? "outline" : "default";
  const dismissPillClass = actionIsPrimary ? PILL_OUTLINE_CLASS : PILL_FILLED_CLASS;
  const unit = useMemo(
    () => normalizeIntervalUnit(currentIntervalUnit),
    [currentIntervalUnit],
  );
  const baseInterval =
    typeof currentInterval === "number" && currentInterval >= 1
      ? Math.floor(currentInterval)
      : null;

  const options = useMemo<number[]>(() => {
    if (baseInterval == null) return [];
    return [baseInterval + 1, baseInterval + 2];
  }, [baseInterval]);

  const [selectedInterval, setSelectedInterval] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedInterval(options[0] ?? null);
    }
  }, [open, options]);

  const canSubmit = unit != null && selectedInterval != null && !confirmPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-80"
        className="tw:z-81 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Change frequency
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
          {unit != null && baseInterval != null ? (
            <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
              Currently every{" "}
              <span className="tw:font-medium">
                {baseInterval} {unitLabel(unit, baseInterval)}
              </span>
              . Pick how often you&apos;d like deliveries instead.
            </p>
          ) : (
            <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
              We couldn&apos;t read your current frequency. Please try again
              later.
            </p>
          )}
        </DialogHeader>
        {unit != null && options.length > 0 ? (
          <fieldset className="tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0 tw:mt-2">
            {options.map((n) => {
              const inputId = `change-frequency-${n}`;
              const selected = selectedInterval === n;
              return (
                <label
                  key={n}
                  htmlFor={inputId}
                  className={cn(
                    "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-4 tw:text-sm tw:transition-colors",
                    selected
                      ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                      : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
                  )}
                >
                  <input
                    id={inputId}
                    type="radio"
                    name="change-frequency"
                    value={n}
                    checked={selected}
                    disabled={confirmPending}
                    onChange={() => setSelectedInterval(n)}
                    className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
                  />
                  <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                    <span
                      className="tw:font-medium tw:leading-snug"
                      style={{ color: "var(--so-text, #523D33)" }}
                    >
                      Every {n} {unitLabel(unit, n)}
                    </span>
                  </div>
                </label>
              );
            })}
          </fieldset>
        ) : null}
        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            variant={actionVariant}
            disabled={!canSubmit}
            onClick={() => {
              if (unit != null && selectedInterval != null) {
                onConfirm(selectedInterval, unit);
              }
            }}
            className={actionPillClass}
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Update frequency"
            )}
          </Button>
          <Button
            type="button"
            variant={dismissVariant}
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={dismissPillClass}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
